import React from 'react';
import { Outlet, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MdArrowBack, MdSettings } from 'react-icons/md';
import { apiGet } from '../services/api';
import useSubscription from '../hooks/useSubscription';
import useAppStore from '../stores/appStore';

// Single-screen shell (Eos-inspired): a fixed 100dvh column with NO bottom tab
// bar. The home route ('/') is the HUD and never scrolls; detail routes are
// full-screen drill-downs reached from the HUD tiles, each with a back arrow.
// Chat manages its own internal scroll, so it stays overflow-hidden like home.

const TITLES = {
  '/chat': 'Chat',
  '/calendar': 'Calendar',
  '/health': 'Health',
  '/money': 'Money',
  '/settings': 'Settings',
};

export default function Layout() {
  const { isPro, isTrial, trialDaysLeft } = useSubscription();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const onboarded = useAppStore((s) => s.onboarded);

  // Route never-onboarded users into first-run setup. Only email registration
  // used to reach /onboarding, so Sign in with Apple users (the primary iOS
  // path) never set a home city — and weather silently vanished everywhere.
  // Server signal, not just the device flag: an untouched profile (no name,
  // no city) plus a device that hasn't completed onboarding → onboard. Users
  // who deliberately skipped both fields pass through once per device via the
  // local flag (skipping sets it).
  const { data: profileData } = useQuery({
    queryKey: ['profile'],
    queryFn: () => apiGet('/me/profile'),
    enabled: !onboarded,
  });
  const profile = profileData?.profile;
  if (!onboarded && profile && !profile.preferredName && !profile.homeCity) {
    return <Navigate to="/onboarding" replace />;
  }

  const isHome = pathname === '/';
  const selfScroll = isHome || pathname === '/chat'; // pages that own their scroll
  const title = TITLES[pathname] || '';

  return (
    <div className="flex flex-col h-[100dvh] overflow-hidden">
      <header className="shrink-0 safe-area-top bg-night-bg/80 backdrop-blur-lg border-b border-primary-400/15">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between gap-2">
          {isHome ? (
            <>
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="brand-text text-lg font-bold uppercase tracking-[0.35em]">Cadence</span>
                {isPro && (
                  <span className="hud-meta !text-primary-300 border border-primary-400/40 rounded-full px-2 py-0.5 shadow-[0_0_10px_rgba(34,211,238,0.25)]">
                    {isTrial ? `Trial${trialDaysLeft != null ? ` · ${trialDaysLeft}d` : ''}` : 'Pro'}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => navigate('/settings')}
                aria-label="Settings"
                className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-night-muted hover:text-primary-300 transition-colors"
              >
                <MdSettings className="w-5 h-5" />
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => navigate('/')}
                aria-label="Back to home"
                className="flex items-center -ml-2 px-2 min-h-[44px] min-w-[44px] text-night-muted hover:text-primary-300 transition-colors"
              >
                <MdArrowBack className="w-5 h-5" />
              </button>
              <span className="font-display font-bold uppercase tracking-[0.2em] text-sm text-primary-300">{title}</span>
              <span className="w-9 shrink-0" aria-hidden="true" />
            </>
          )}
        </div>
      </header>

      <main
        className={`flex-1 min-h-0 w-full max-w-2xl mx-auto px-4 py-4 flex flex-col ${
          selfScroll ? 'overflow-hidden' : 'overflow-y-auto'
        }`}
      >
        <Outlet />
      </main>
    </div>
  );
}
